import type { IssueKind } from "@hammer/core";
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Price, TimeLeft } from "../components/lot-bits.tsx";
import { ResultForm } from "../components/ResultForm.tsx";
import { api, type Issue } from "../lib/api.ts";
import { keys, useIssues, useLots } from "../lib/queries.ts";
import { refreshLot } from "../lib/refresh.ts";
import { toast } from "../lib/store.ts";

const SECTIONS: { kinds: IssueKind[]; title: string; blurb: string }[] = [
  { kinds: ["fetch_failing", "stale"], title: "Prices you can't trust right now", blurb: "Hammer couldn't check these recently. The numbers shown may be old." },
  { kinds: ["record_result"], title: "Results to record", blurb: "These have closed. Recording what they sold for builds your price history." },
  { kinds: ["outbid"], title: "Over your max", blurb: "" },
  { kinds: ["low_confidence_parse", "suspicious_value", "end_time_past"], title: "Details to check", blurb: "Something about these lots looked off when Hammer read them." },
  { kinds: ["source_broken"], title: "Sites not answering", blurb: "Most recent checks to these sites failed." },
];

export function Inbox() {
  const issuesQ = useIssues();
  const lots = useLots().data ?? [];
  const issues = issuesQ.data ?? [];
  const byId = new Map(lots.map((l) => [l.id, l]));

  if (issuesQ.isPending && !issuesQ.data) return <div className="skeleton h-8 w-60" aria-busy />;

  return (
    <div className="grid gap-2 max-w-[60rem]">
      <h1 className="heading text-[26px]">Inbox</h1>
      {!issues.length && <p className="muted">Nothing needs you. Hammer will put things here when a price can't be checked, a lot closes, or something it read looks wrong.</p>}
      {SECTIONS.map((s) => {
        const items = issues.filter((i) => s.kinds.includes(i.kind));
        if (!items.length) return null;
        return (
          <section key={s.title} className="mt-6">
            <h2 className="group-h heading !mt-0">{s.title}<span className="n num">{items.length}</span></h2>
            {s.blurb && <p className="muted text-[14px] mb-2">{s.blurb}</p>}
            <ul>
              {items.map((i) => <IssueItem key={i.id} issue={i} lot={i.lotId ? byId.get(i.lotId) : undefined} />)}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function IssueItem({ issue, lot }: { issue: Issue; lot: ReturnType<typeof useLots>["data"] extends (infer T)[] | undefined ? T | undefined : never }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const resolve = async (resolution: "dismissed" | "kept_closed" | "archived" | "retried", msg: string) => {
    setBusy(true);
    try {
      await api(`/issues/${issue.id}/resolve`, { method: "POST", body: { resolution } });
      toast(msg);
    } catch (e) {
      toast(`Couldn't update: ${(e as Error).message}`, { tone: "error" });
    }
    await Promise.all([qc.invalidateQueries({ queryKey: keys.issues }), qc.invalidateQueries({ queryKey: keys.lots })]);
    setBusy(false);
  };

  return (
    <li className="border-t border-rule py-4 grid gap-3">
      <div className="flex justify-between gap-4 items-start">
        <div className="min-w-0">
          {issue.lotId ? (
            <Link to="/lot/$id" params={{ id: issue.lotId }} className="r-title">{issue.lotTitle ?? "Untitled lot"}</Link>
          ) : (
            <Link to="/sources" className="r-title">{issue.source}</Link>
          )}
          <p className="text-[14px] mt-0.5">{issue.detail}</p>
        </div>
        {lot && issue.kind !== "record_result" && (
          <div className="text-right shrink-0 hidden sm:block"><Price lot={lot} /><div className="mt-1"><TimeLeft lot={lot} /></div></div>
        )}
      </div>

      {issue.kind === "record_result" && lot ? (
        <ResultForm lot={lot} />
      ) : (
        <div className="flex flex-wrap gap-2">
          {(issue.kind === "fetch_failing" || issue.kind === "stale") && lot && (
            <>
              {lot.reach !== "capture" && <button className="btn btn-primary" disabled={busy} onClick={async () => { setBusy(true); await refreshLot(qc, lot.id); setBusy(false); }}>Check now</button>}
              <a className={`btn ${lot.reach === "capture" ? "btn-primary" : ""}`} href={lot.url} target="_blank" rel="noreferrer noopener">Open the lot page</a>
            </>
          )}
          {(issue.kind === "low_confidence_parse" || issue.kind === "suspicious_value") && issue.lotId && (
            <>
              <Link className="btn btn-primary" to="/lot/$id" params={{ id: issue.lotId }}>Review the lot</Link>
              <button className="btn" disabled={busy} onClick={() => resolve("dismissed", "Marked as fine")}>It's fine</button>
            </>
          )}
          {issue.kind === "end_time_past" && (
            <>
              <button className="btn btn-primary" disabled={busy} onClick={() => resolve("kept_closed", "Marked as closed")}>It's closed</button>
              <button className="btn" disabled={busy} onClick={() => resolve("archived", "Archived")}>Archive it</button>
            </>
          )}
          {issue.kind === "source_broken" && <Link className="btn" to="/sources">See sources</Link>}
          {!["low_confidence_parse", "suspicious_value", "end_time_past"].includes(issue.kind) && (
            <button className="btn btn-quiet" disabled={busy} onClick={() => resolve("dismissed", "Dismissed")}>Dismiss</button>
          )}
        </div>
      )}
    </li>
  );
}
